/**
 * Shared polling helper for portal_report_jobs rows.
 *
 * Status values are 'queued' | 'running' | 'succeeded' | 'failed'.
 * The helper repeatedly selects the row until status reaches a terminal
 * state or the timeout elapses.
 */

import type { SupabaseClient } from '@supabase/supabase-js';

export type JobStatus = 'queued' | 'running' | 'succeeded' | 'failed';

export interface PortalReportJobRow {
  id: string;
  status: JobStatus;
  result_jsonb: Record<string, unknown> | null;
  storage_key: string | null;
  error: string | null;
  created_at: string;
  updated_at: string;
}

export class JobPollTimeoutError extends Error {
  constructor(public readonly jobId: string) {
    super(`Job ${jobId} did not finish within the timeout`);
    this.name = 'JobPollTimeoutError';
  }
}

export class JobPollAbortedError extends Error {
  constructor(public readonly jobId: string) {
    super(`Polling for job ${jobId} was aborted by the caller`);
    this.name = 'JobPollAbortedError';
  }
}

/**
 * Poll a portal_report_jobs row until it finishes or times out.
 * Resolves with the final row when status becomes 'succeeded' or 'failed'.
 * Rejects with JobPollTimeoutError if the timeout elapses first.
 *
 * @param supabase  A Supabase client obtained via AuthStore.getSupabaseClient().
 * @param jobId     The portal_report_jobs.id to watch.
 * @param intervalMs How often to re-select the row (ms).
 * @param timeoutMs  Give up after this many ms.
 * @param opts.shouldStop Checked after each sleep; when true, rejects with
 *   JobPollAbortedError instead of issuing another select (caller teardown).
 */
export async function pollJob(
  supabase: SupabaseClient,
  jobId: string,
  intervalMs: number,
  timeoutMs: number,
  opts?: { shouldStop?: () => boolean },
): Promise<PortalReportJobRow> {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
    if (opts?.shouldStop?.()) throw new JobPollAbortedError(jobId);

    const { data, error } = await supabase
      .from('portal_report_jobs')
      .select('id, status, result_jsonb, storage_key, error, created_at, updated_at')
      .eq('id', jobId)
      .single();

    if (error) throw error;
    if (!data) continue;

    if (data.status === 'succeeded' || data.status === 'failed') {
      return data as PortalReportJobRow;
    }
  }
  throw new JobPollTimeoutError(jobId);
}

/**
 * Look up the most recent in-flight (queued or running) job for a given
 * pseudobrand + job_type. Used to resume progress UI after a page refresh
 * or dialog dismiss. Returns null when nothing in flight.
 */
export async function findInFlightJob(
  supabase: SupabaseClient,
  jobType: string,
  segmentId: string,
): Promise<Pick<PortalReportJobRow, 'id' | 'status' | 'created_at'> & { params: Record<string, unknown> } | null> {
  const { data, error } = await supabase
    .from('portal_report_jobs')
    .select('id, status, params, created_at')
    .eq('params->>job_type', jobType)
    .eq('params->>pseudobrand_id', segmentId)
    .in('status', ['queued', 'running'])
    .order('created_at', { ascending: false })
    .limit(1);

  if (error) throw error;
  return data && data.length ? (data[0] as any) : null;
}
